import type { RequestOptions } from "@/api/client";
import { ApiError, AuthError } from "@/api/errors";
import { mockData } from "./data";
import type { PaginationMeta } from "@/api/types";

/**
 * Local mock API adapter. Matches the shape of the documented PHP API and can
 * temporarily power production builds while the remote API is unavailable.
 */
export async function mockApi<T>(path: string, options: RequestOptions = {}): Promise<T> {
  await new Promise((r) => setTimeout(r, 90));
  const method = options.method ?? "GET";
  const bare = path.split("?")[0];
  const key = `${method} ${bare}`;
  const handler = handlers[key] ?? matchDynamic(method, bare);
  if (!handler) {
    // For any unknown admin/player/coaching endpoint we return a benign empty
    // envelope so admin scaffolding pages don't crash; mutations POST/PATCH/DELETE
    // fall through to a mock-ok response.
    if (method !== "GET") return { ok: true } as T;
    console.warn(`[mock] no handler for ${key} — returning empty payload`);
    return (bare.includes("dashboard") ? {} : []) as T;
  }
  return handler(path, options) as T;
}

type Handler = (path: string, opts: RequestOptions) => unknown;

const paginate = <T>(items: T[], page = 1, perPage = 20) => {
  const start = (page - 1) * perPage;
  return {
    data: items.slice(start, start + perPage),
    meta: {
      currentPage: page,
      perPage,
      total: items.length,
      lastPage: Math.max(1, Math.ceil(items.length / perPage)),
    } satisfies PaginationMeta,
  };
};

const num = (q: RequestOptions["query"], key: string, d = 1) =>
  Number((q as Record<string, unknown> | undefined)?.[key]) || d;

const handlers: Record<string, Handler> = {
  "GET /auth/me": () => {
    if (!mockData.session) throw new AuthError();
    return mockData.session;
  },
  "POST /auth/login": (_p, opts) => {
    const body = opts.body as { email?: string } | undefined;
    // Any email/password works. Route to a demo user based on the local-part.
    const local = (body?.email ?? "admin").split("@")[0].toLowerCase();
    const target = local.includes("coach")
      ? mockData.demoUsers.coach
      : local.includes("player")
        ? mockData.demoUsers.player
        : local.includes("match") || local.includes("ops") || local.includes("stream")
          ? mockData.demoUsers.matchop
          : mockData.demoUsers.admin;
    mockData.session = { ...target, email: body?.email ?? target.email };
    return mockData.session;
  },
  "POST /auth/logout": () => {
    mockData.session = null;
    return { ok: true };
  },
  "GET /auth/csrf": () => ({ token: "mock-csrf" }),
  "GET /public/settings": () => mockData.settings,
  "GET /public/navigation": () => mockData.navigation,
  "GET /public/home": () => mockData.home,
  "GET /public/teams": () => mockData.teams,
  "GET /public/players": (_p, opts) =>
    paginate(mockData.players, num(opts.query, "page"), num(opts.query, "perPage", 30)),
  "GET /public/staff": () => mockData.staff,
  "GET /public/fixtures": (_p, opts) =>
    paginate(
      mockData.fixtures.filter((f) => f.status !== "full_time"),
      num(opts.query, "page"),
      num(opts.query, "perPage", 20),
    ),
  "GET /public/results": (_p, opts) =>
    paginate(mockData.results, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /public/live-match": () => mockData.liveFixture,
  "GET /public/league-tables": () => mockData.leagueTables,
  "GET /public/streams": (_p, opts) =>
    paginate(mockData.streams, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /public/articles": (_p, opts) =>
    paginate(mockData.articles, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /public/videos": (_p, opts) =>
    paginate(mockData.videos, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /public/galleries": (_p, opts) =>
    paginate(mockData.galleries, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /public/sponsors": () => mockData.sponsors,
  "GET /admin/dashboard": () => ({
    liveMatch: mockData.liveFixture,
    nextFixture: mockData.fixtures.find((f) => f.status === "scheduled"),
    latestResult: mockData.results[0],
    leaguePosition: mockData.leagueTables[0].rows.find((r) => r.isClub)?.position,
    form: ["W", "W", "D", "W", "L"],
    activeStream: mockData.streams.find((s) => s.status === "live"),
    scheduledStreams: mockData.streams.filter((s) => s.status === "scheduled").length,
    newEnquiries: mockData.enquiries.filter((e) => e.status === "new").length,
    draftArticles: 3,
    pendingApprovals: 5,
    recentMedia: mockData.galleries[0]?.items?.slice(0, 6) ?? [],
    trainingAttendance: 92,
    availablePlayers: 22,
    storageUsedGb: 128,
    storageTotalGb: 500,
    analytics: mockData.analytics,
    auditLogs: mockData.auditLogs.slice(0, 6),
  }),
  "GET /admin/users": (_p, opts) =>
    paginate(mockData.portalUsers, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /admin/roles": () => [
    {
      publicId: "role_super",
      name: "Super Administrator",
      userCount: 1,
      description: "Unrestricted platform access.",
    },
    {
      publicId: "role_admin",
      name: "Club Administrator",
      userCount: 2,
      description: "Full club administration.",
    },
    {
      publicId: "role_team",
      name: "Team Administrator",
      userCount: 3,
      description: "Team-scoped administration.",
    },
    {
      publicId: "role_hc",
      name: "Head Coach",
      userCount: 1,
      description: "Coaching, selection and assessments.",
    },
    {
      publicId: "role_ac",
      name: "Assistant Coach",
      userCount: 2,
      description: "Coaching support.",
    },
    {
      publicId: "role_media",
      name: "Media Manager",
      userCount: 1,
      description: "Editorial and media library management.",
    },
    {
      publicId: "role_editor",
      name: "Content Editor",
      userCount: 4,
      description: "Article publishing.",
    },
    { publicId: "role_photo", name: "Photographer", userCount: 2, description: "Media uploads." },
    {
      publicId: "role_match",
      name: "Match Operator",
      userCount: 2,
      description: "Live match control.",
    },
    {
      publicId: "role_stream",
      name: "Stream Operator",
      userCount: 2,
      description: "Stream control.",
    },
    {
      publicId: "role_player",
      name: "Player",
      userCount: 25,
      description: "Player self-service portal.",
    },
    {
      publicId: "role_sponsor",
      name: "Sponsor Representative",
      userCount: 4,
      description: "Sponsor reporting access.",
    },
  ],
  "GET /admin/permissions": () => ({
    modules: [
      "Users",
      "Roles",
      "Players",
      "Teams",
      "Staff",
      "Fixtures",
      "Matches",
      "Streams",
      "Articles",
      "Media",
      "Galleries",
      "Sponsors",
      "Forms",
      "Enquiries",
      "Reports",
      "Audit",
      "Settings",
    ],
    actions: ["view", "create", "edit", "delete", "publish", "approve", "export"],
  }),
  "GET /admin/enquiries": (_p, opts) =>
    paginate(mockData.enquiries, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /admin/audit-logs": (_p, opts) =>
    paginate(mockData.auditLogs, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /admin/reports": () => mockData.analytics,
  "GET /admin/analytics": () => mockData.analytics,
  "GET /admin/settings": () => mockData.settings,
  "GET /admin/forms": () => mockData.formsCatalogue,
  "GET /admin/players": (_p, opts) =>
    paginate(mockData.players, num(opts.query, "page"), num(opts.query, "perPage", 25)),
  "GET /admin/staff": () => mockData.staff,
  "GET /admin/teams": () => mockData.teams,
  "GET /admin/fixtures": (_p, opts) =>
    paginate(
      [...mockData.fixtures, ...mockData.results],
      num(opts.query, "page"),
      num(opts.query, "perPage", 25),
    ),
  "GET /admin/streams": (_p, opts) =>
    paginate(mockData.streams, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /admin/articles": (_p, opts) =>
    paginate(mockData.articles, num(opts.query, "page"), num(opts.query, "perPage", 20)),
  "GET /admin/media": (_p, opts) =>
    paginate(
      mockData.galleries.flatMap((g) => g.items ?? []),
      num(opts.query, "page"),
      num(opts.query, "perPage", 24),
    ),
  "GET /admin/galleries": () => mockData.galleries,
  "GET /admin/sponsors": () => mockData.sponsors,
  "GET /player/dashboard": () => {
    const player = mockData.players[5]; // Kagiso Dlamini
    return {
      player,
      nextFixture: mockData.fixtures.find((f) => f.status === "scheduled"),
      nextTraining: mockData.trainingSessions.find((t) => new Date(t.scheduledStart) > new Date()),
      seasonStats: {
        appearances: 12,
        goals: 4,
        assists: 3,
        minutes: 1042,
        yellows: 2,
        reds: 0,
        rating: 7.4,
      },
      attendancePercent: 96,
      notifications: mockData.notifications.slice(0, 4),
      assignedVideos: mockData.videos.slice(0, 3),
      profileCompletion: 82,
    };
  },
  "GET /player/profile": () => mockData.players[5],
  "GET /player/fixtures": () => mockData.fixtures.filter((f) => f.status !== "full_time"),
  "GET /player/results": () => mockData.results,
  "GET /player/statistics": () => ({
    season: "2025/26",
    appearances: 12,
    goals: 4,
    assists: 3,
    minutes: 1042,
    yellows: 2,
    reds: 0,
    ratingTrend: Array.from({ length: 12 }, (_, i) => ({
      match: i + 1,
      rating: 6.5 + Math.sin(i / 2) * 0.8 + 0.1 * i,
    })),
  }),
  "GET /player/training-sessions": () => mockData.trainingSessions,
  "GET /player/attendance": () =>
    mockData.trainingAttendance.filter((a) => a.playerPublicId === mockData.players[5].publicId),
  "GET /player/documents": () => [
    {
      publicId: "doc_handbook",
      title: "Player Handbook 2025/26",
      type: "pdf",
      updatedAt: new Date().toISOString(),
      sizeKb: 812,
    },
    {
      publicId: "doc_kit",
      title: "Kit collection schedule",
      type: "pdf",
      updatedAt: new Date().toISOString(),
      sizeKb: 220,
    },
    {
      publicId: "doc_medical",
      title: "Medical & consent forms",
      type: "pdf",
      updatedAt: new Date().toISOString(),
      sizeKb: 340,
    },
  ],
  "GET /player/videos": () => mockData.videos.slice(0, 6),
  "GET /player/announcements": () => mockData.notifications,
  "GET /player/notifications": () => mockData.notifications,
  "GET /coaching/dashboard": () => ({
    nextMatch: mockData.fixtures.find((f) => f.status === "scheduled"),
    trainingAttendancePercent: 92,
    availablePlayers: 22,
    suspensions: 1,
    recentResults: mockData.results.slice(0, 4),
    pendingAssessments: 3,
    recentVideoUploads: mockData.videos.slice(0, 4),
  }),
  "GET /coaching/training-sessions": () => mockData.trainingSessions,
  "GET /coaching/match-selections": () => mockData.fixtures.slice(0, 4),
  "GET /coaching/player-assessments": () => mockData.playerAssessments,
  "GET /coaching/reports": () => mockData.analytics,
};

function matchDynamic(method: string, path: string): Handler | null {
  const m = (re: RegExp) => path.match(re);
  if (method !== "GET") return null;
  let match;
  if ((match = m(/^\/public\/teams\/([^/]+)$/)))
    return () =>
      mockData.teams.find((t) => t.publicId === match![1] || t.slug === match![1]) ?? notFound();
  if ((match = m(/^\/public\/players\/([^/]+)$/)))
    return () =>
      mockData.players.find((p) => p.publicId === match![1] || p.slug === match![1]) ?? notFound();
  if ((match = m(/^\/public\/fixtures\/([^/]+)$/)))
    return () =>
      [...mockData.fixtures, ...mockData.results].find((f) => f.publicId === match![1]) ??
      notFound();
  if ((match = m(/^\/public\/matches\/([^/]+)\/live-state$/)))
    return () => mockData.buildLiveState(match![1]);
  if ((match = m(/^\/public\/matches\/([^/]+)$/)))
    return () => mockData.buildMatchDetail(match![1]);
  if ((match = m(/^\/public\/streams\/([^/]+)$/)))
    return () => mockData.streams.find((s) => s.publicId === match![1]) ?? notFound();
  if ((match = m(/^\/public\/articles\/([^/]+)$/)))
    return () =>
      mockData.articles.find((a) => a.slug === match![1] || a.publicId === match![1]) ?? notFound();
  if ((match = m(/^\/public\/galleries\/([^/]+)$/)))
    return () => mockData.galleries.find((g) => g.publicId === match![1]) ?? notFound();
  if ((match = m(/^\/admin\/players\/([^/]+)$/)))
    return () => mockData.players.find((p) => p.publicId === match![1]) ?? notFound();
  if ((match = m(/^\/admin\/users\/([^/]+)$/)))
    return () => mockData.portalUsers.find((u) => u.publicId === match![1]) ?? notFound();
  if ((match = m(/^\/admin\/matches\/([^/]+)$/))) return () => mockData.buildMatchDetail(match![1]);
  if ((match = m(/^\/coaching\/teams\/([^/]+)$/)))
    return () => mockData.teams.find((t) => t.publicId === match![1]) ?? notFound();
  return null;
}

function notFound(): never {
  throw new ApiError("Not found (mock).", 404, "NOT_FOUND");
}
